import useSWRInfinite from "swr/infinite"

import {
    ORDER_BY_KW,
    ORDER_KW,
    TTR_DEFAULT_ORDER_BY,
    TTR_DEFAULT_ORDER_DIRECTION,
} from "../../../constants/generalConstants"
import { parseAndThrowAppError } from "../../../errors/errorProcessors"
import { useGetUserData } from "../../../hooks/useGetUserData"
import { canAccessProtectedRoutes } from "../../../utils/auth/authUtils"
import { getClientProtected, getClientPublic } from "../clients/clients"
import {
    IDatabaseSearchResults,
    searchGenDevicesMapper,
} from "../mappers/searchGenDevicesMapper"
import { SEARCH_CONFIG } from "../swrConfig"
import { SEARCH_GEN_DEVICES } from "../urls/hybrid"

export const GEN_PAGE_SIZE = 20

export type TMultiFetcherUseSearchGenDevices = {
    data: IDatabaseSearchResults | undefined
    isLoading: boolean
    isError: boolean
    isValidating: boolean
    page: number
    setPage: (
        size: number | ((_size: number) => number)
    ) => Promise<IDatabaseSearchResults[] | undefined>
    isFinalPage: (pageIndex: number, totalResults: number) => boolean
    mutate: () => void
}

/**
 * Hook for calling search API for generation devices. Checks authentication
 * status to determine if public / protected API should be used. Public users
 * should not be directed to the protected routes.
 * @returns {TMultiFetcherUseSearchGenDevices} - SWR data, loading and error states.
 */
const useMultiFetcherSearchGenDevices = (
    searchParams: URLSearchParams
): TMultiFetcherUseSearchGenDevices => {
    const { isSignedIn, isConfiguring, groups } = useGetUserData()

    const canAccessProtected = canAccessProtectedRoutes(isSignedIn, groups)

    const isFinalPage = (pageIndex: number, totalResults: number): boolean =>
        pageIndex >= Math.ceil(totalResults / GEN_PAGE_SIZE)
    /**
     * Generates URL query key for pagination. If auth status is configuring,
     * the request should be skipped. Once authentication is complete the hook
     * will rerender, at which point an API request will be made.
     * @param pageIndex - index of current page
     * @param previousPageData  - data output from previous page
     *
     * This hook has a conditional fetcher, which routes requests through
     * either the public or protected axios client based on auth status  and role.
     * The request is deferred if the auth status is `isConfiguring`.
     * Therefore, the `isLoading` output value is augmented with this
     * parameter for this hook.
     */
    const getKey = (
        pageIndex: number,
        previousPageData: IDatabaseSearchResults
    ): string | null => {
        if (isConfiguring) {
            return null
        }
        if (previousPageData) {
            if (isFinalPage(pageIndex, previousPageData.totalResults)) {
                return null
            }
        }
        // Page information should not be appended onto global URL params. A
        // separate local params object is instantiated to avoid mutating global state
        const queryParams = new URLSearchParams(searchParams.toString())
        queryParams.set("skip", String(pageIndex * GEN_PAGE_SIZE))
        queryParams.set("limit", String(GEN_PAGE_SIZE))
        if (!queryParams.get(ORDER_BY_KW)) {
            queryParams.set(ORDER_BY_KW, TTR_DEFAULT_ORDER_BY)
        }
        if (!queryParams.get(ORDER_KW)) {
            queryParams.set(ORDER_KW, TTR_DEFAULT_ORDER_DIRECTION)
        }
        return SEARCH_GEN_DEVICES(queryParams.toString(), canAccessProtected)
    }

    const {
        data: pageResults,
        error,
        isLoading,
        isValidating,
        size,
        setSize,
        mutate,
    } = useSWRInfinite(
        getKey,
        async (url: string): Promise<IDatabaseSearchResults> => {
            try {
                const fetcher = canAccessProtected
                    ? getClientProtected
                    : getClientPublic
                const res = await fetcher(url)
                return searchGenDevicesMapper.map(res)
            } catch (err) {
                throw parseAndThrowAppError(err)
            }
        },
        SEARCH_CONFIG
    )

    return {
        data: searchGenDevicesMapper.mergePaginatedData(pageResults),
        isLoading: isLoading || isConfiguring, // See notes in docstring
        isError: error,
        isValidating,
        page: size,
        setPage: setSize,
        isFinalPage,
        mutate,
    }
}

export default useMultiFetcherSearchGenDevices
